import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';

export default function ArticleLayout({
  title,
  metaTitle,
  metaDescription,
  canonicalPath,
  introLead,
  introAnswer,
  tableOfContents = [],
  checklistTitle = 'Tjekliste: Det kan du selv gøre',
  checklist = [],
  diagram,
  sections = [],
  proCallout,
  faq = [],
  internalLinks = [],
  sources = [],
  datePublished,
  dateModified,
}) {
  return (
    <article className="article-container text-[#221E1B]">
      <SeoHead
        title={metaTitle || title}
        description={metaDescription}
        canonicalPath={canonicalPath}
        faqs={faq}
        datePublished={datePublished}
        dateModified={dateModified}
      />

      {/* Overskrift h1 */}
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#221E1B] leading-tight mb-6">
        {title}
      </h1>

      {/* Første to afsnit med det direkte svar */}
      <div className="space-y-4 text-[17px] leading-[1.65] mb-8">
        <p className="text-[#221E1B] font-medium">{introLead}</p>
        <p className="text-[#221E1B]">{introAnswer}</p>
      </div>

      {/* Kompakt indholdsfortegnelse (Table of Contents) i #F3E6DC */}
      {tableOfContents.length > 0 && (
        <nav aria-label="Indholdsfortegnelse" className="bg-[#F3E6DC] border border-[#E6DED5] rounded p-5 my-8">
          <div className="text-xs uppercase tracking-wider font-bold text-[#B5531E] mb-3">
            Indhold på siden
          </div>
          <ul className="space-y-1.5 text-sm">
            {tableOfContents.map((item, idx) => (
              <li key={idx}>
                <a
                  href={`#${item.id}`}
                  className="text-[#221E1B] hover:text-[#B5531E] hover:underline underline-offset-4"
                >
                  {item.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Valgfrit diagram */}
      {diagram && <div className="my-8">{diagram}</div>}

      {/* Nummereret tjekliste: stort accenttal, én sætning pr. trin */}
      {checklist.length > 0 && (
        <section id="tjekliste" className="my-10">
          <h2 className="text-xl font-bold text-[#221E1B] mb-5 tracking-tight">
            {checklistTitle}
          </h2>
          <ol className="space-y-4">
            {checklist.map((step, idx) => (
              <li
                key={idx}
                className="flex items-start gap-4 p-4 bg-[#F7F3EE] border border-[#E6DED5] rounded"
              >
                <span className="font-mono text-xl sm:text-2xl font-bold text-[#B5531E] leading-none shrink-0 w-8 pt-0.5">
                  {idx + 1}.
                </span>
                <div className="flex-1">
                  <strong className="block text-[#221E1B] font-semibold text-[17px] mb-1">
                    {step.title}
                  </strong>
                  <p className="text-[17px] text-[#221E1B] leading-[1.65]">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Dybdegående sektioner */}
      {sections.map((section, idx) => (
        <section key={idx} id={section.id} className="my-10 space-y-4">
          <h2 className="text-xl font-bold text-[#221E1B] tracking-tight">
            {section.title}
          </h2>
          <div className="space-y-4 text-[17px] leading-[1.65] text-[#221E1B]">
            {section.content}
          </div>
        </section>
      ))}

      {/* Ring til en fagmand boks (samme tint #F3E6DC, bold label, ingen rød advarselsfarve) */}
      {proCallout && (
        <div className="bg-[#F3E6DC] border border-[#E6DED5] rounded p-6 my-10 space-y-2">
          <div className="text-base font-bold text-[#221E1B] tracking-tight">
            {proCallout.title || 'Hvornår skal du tilkalde en fagmand?'}
          </div>
          <div className="text-[16px] text-[#221E1B] leading-[1.65] space-y-2">
            {proCallout.content}
          </div>
        </div>
      )}

      {/* FAQ sektion med 3-5 spørgsmål */}
      {faq.length > 0 && (
        <section id="faq" className="my-12 border-t border-[#E6DED5] pt-8">
          <h2 className="text-xl font-bold text-[#221E1B] tracking-tight mb-6">
            Ofte stillede spørgsmål
          </h2>
          <div className="space-y-6">
            {faq.map((item, idx) => (
              <div key={idx} className="space-y-2">
                <h3 className="text-base font-bold text-[#221E1B]">
                  {item.q}
                </h3>
                <p className="text-[17px] text-[#221E1B] leading-[1.65]">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Interne links: 3-6 med beskrivende linktekst */}
      {internalLinks.length > 0 && (
        <section className="my-10 border-t border-[#E6DED5] pt-8">
          <h3 className="text-sm uppercase tracking-wider font-bold text-[#6B625B] mb-3">
            Relevante vejledninger
          </h3>
          <ul className="space-y-2 text-[17px]">
            {internalLinks.map((link, idx) => (
              <li key={idx}>
                <Link
                  to={link.to}
                  className="text-[#B5531E] hover:underline underline-offset-4 font-medium"
                >
                  {link.text}
                </Link>
                {link.desc && (
                  <span className="text-[#6B625B] text-sm ml-2">
                    – {link.desc}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Kilder nederst med links til de faktisk hentede kilder */}
      <footer className="mt-12 pt-6 border-t border-[#E6DED5] text-xs text-[#6B625B]">
        <span className="font-semibold text-[#221E1B]">Kilder:</span>{' '}
        {sources.map((s, idx) => (
          <span key={idx}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6B625B] hover:text-[#B5531E] underline"
            >
              {s.label}
            </a>
            {idx < sources.length - 1 ? ' · ' : ''}
          </span>
        ))}
      </footer>
    </article>
  );
}
